import * as T from 'three'
import type { Bed } from './beds'
import { customGeometry, customVerticalProfile, CUSTOM, type Rect } from './custom-layout'

/** Visual millwork concept. The modeled core is not an engineered load-bearing connection. */
export function addRecessedArchLibrary(root:T.Group,bed:Bed,s:Rect & {height:number}) {
  const library = new T.Group()
  library.name = 'built-in-bookshelf'
  library.userData.measurementName = 'Recessed arch library'
  const underside = customVerticalProfile(bed).deckHeight - CUSTOM.frameDepth
  const front = Math.max(...customGeometry(bed).uppers.map(r=>r.y+r.h))
  library.userData.dimensionsInches = {width:s.w,depth:front,height:underside}
  library.userData.shelfDepthInches = 12
  library.userData.overheadSweepInches = front - 12
  const body = new T.Group(); body.name = 'arch-library-shallow-body'; library.add(body)
  const material = ()=>new T.MeshStandardMaterial({color:0xffffff,roughness:.72})
  function mesh(parent:T.Group,name:string,geometry:T.BufferGeometry) {
    const m=new T.Mesh(geometry,material());m.name=name;m.userData.finishPart='frame';m.castShadow=true;m.receiveShadow=true;parent.add(m);return m
  }
  function box(parent:T.Group,name:string,x:number,y:number,z:number,w:number,h:number,d:number) {
    const m=mesh(parent,name,new T.BoxGeometry(w/12,h/12,d/12));m.position.set(x/12,y/12,z/12);return m
  }
  // Both rotated niche carcasses stay entirely in the first 12 inches from the wall.
  const core=4, angle=25*Math.PI/180, depth=5.5, projected=(s.w-core)/2
  const width=(projected-depth*Math.sin(angle))/Math.cos(angle)
  const height=underside, t=.75
  box(body,'arch-library-center-spine',0,underside/2,6,core,underside,12)
  for(const side of [-1,1]) {
    const niche=new T.Group();niche.name=side<0?'arch-library-left-niche':'arch-library-right-niche'
    niche.rotation.y=side*angle;niche.position.set(side*(core/2+projected/2)/12,0,6/12);body.add(niche)
    box(niche,'niche-back',0,height/2,-depth/2+.125,width,height,.25)
    for(const edge of [-1,1])box(niche,'niche-side',edge*(width-t)/2,height/2,0,t,height,depth)
    box(niche,'niche-plinth',0,2,0,width,4,depth)
    box(niche,'niche-top',0,height-t/2,0,width,t,depth)
    // Arched opening cut through the face panel, not a painted-on arch.
    const outline=new T.Shape();const w=width/12,h=height/12
    outline.moveTo(-w/2,0);outline.lineTo(w/2,0);outline.lineTo(w/2,h);outline.lineTo(-w/2,h);outline.closePath()
    const radius=(width-2*t)/24, spring=(height-t)/12-radius, bottom=4/12
    const hole=new T.Path();hole.moveTo(-radius,bottom);hole.lineTo(-radius,spring);hole.absarc(0,spring,radius,Math.PI,0,true);hole.lineTo(radius,bottom);hole.closePath();outline.holes.push(hole)
    const face=mesh(niche,'niche-arched-face',new T.ExtrudeGeometry(outline,{depth:t/12,bevelEnabled:false,curveSegments:32}));face.position.z=(depth/2-t)/12
    for(let i=0;i<3;i++)box(niche,'niche-shelf-'+i,0,4+t/2+i*(height-8)/3,0,width-2*t,t,depth-.5)
  }
  // A quarter-ellipse meets the shallow spine vertically and the bunk underside horizontally.
  // Bury the tip 1/8 inch into the fascia: no exposed front strip below its bottom edge.
  // Current cabin depth is 44", so the exact sweep beyond the 12" cabinet is 32".
  const vertices:number[]=[],indices:number[]=[],segments=64
  const springHeight=12, top=underside+.125
  // Keep the front flare within the middle half of the library; shelves stay exposed.
  const flareWidth=s.w/2
  for(let i=0;i<=segments;i++) {
    const theta=i/segments*Math.PI/2
    const u=1-Math.cos(theta),z=12+(front-12)*u
    const half=(core+(flareWidth-core)*Math.pow(u,.7))/2
    const bottom=springHeight+(top-springHeight)*Math.sin(theta)
    for(const [x,y] of [[-half,bottom],[half,bottom],[-half,top],[half,top]])vertices.push(x/12,y/12,z/12)
    if(i<segments){const a=i*4,b=a+4;indices.push(a,a+1,b+1,a,b+1,b,a+2,b+2,b+3,a+2,b+3,a+3,a,b,a+2,a+2,b,b+2,a+1,a+3,b+3,a+1,b+3,b+1)}
  }
  indices.push(0,2,3,0,3,1)
  // The front closes at zero thickness inside the fascia; omit its degenerate end cap.
  const geometry=new T.BufferGeometry();geometry.setAttribute('position',new T.Float32BufferAttribute(vertices,3));geometry.setIndex(indices);geometry.computeVertexNormals()
  mesh(library,'arch-library-overhead-sweep',geometry)
  library.position.set((s.x+s.w/2-bed.width/2)/12,0,-bed.depth/24)
  root.add(library)
}


